import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Play } from "lucide-react";
import { CASE_STUDIES } from "../data/content";
import { MaskedLines, Reveal } from "../components/Motion";
import { CtaBand } from "../components/Cta";

export default function CaseStudy() {
  const { slug } = useParams();
  const index = CASE_STUDIES.findIndex((cs) => cs.slug === slug);
  if (index === -1) return <Navigate to="/work" replace />;
  const cs = CASE_STUDIES[index];
  const next = CASE_STUDIES[(index + 1) % CASE_STUDIES.length];

  return (
    <>
      <section className="page-hero tall" data-testid="case-hero">
        <div className="ph-image" style={{ backgroundImage: `url(${cs.heroImage})` }} />
        <div className="ph-overlay" />
        <div className="ph-content">
          <p className="crumb">
            <Link to="/work" data-testid="case-crumb-work">Work</Link> <span>/</span> {cs.title}
          </p>
          <h1>
            <MaskedLines lines={[cs.title]} delay={0.2} />
          </h1>
          <div className="ph-tags">
            {cs.tags.map((tag) => (
              <span className="tag gold" key={tag}>
                {tag}
              </span>
            ))}
            <span className="tag">{cs.year}</span>
          </div>
        </div>
      </section>

      <section className="case-meta section-pad" data-testid="case-meta">
        <Reveal className="meta-bar">
          <div className="mb">
            <span>Client</span>
            <strong>{cs.client}</strong>
          </div>
          <div className="mb">
            <span>Scope</span>
            <strong>{cs.scope}</strong>
          </div>
          <div className="mb">
            <span>Year</span>
            <strong>{cs.year}</strong>
          </div>
          <div className="mb">
            <span>Crafts</span>
            <strong>{cs.tags.join(" · ")}</strong>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="case-summary">{cs.summary}</p>
        </Reveal>
      </section>

      <section className="section-pad" data-testid="case-showreel-section">
        <Reveal>
          <div className="showreel" data-testid="case-showreel">
            {cs.showreelUrl ? (
              <video className="showreel-video" src={cs.showreelUrl} poster={cs.showreelPoster} controls playsInline preload="none" data-testid="case-showreel-video" />
            ) : (
              <>
                <img src={cs.showreelPoster} alt={`${cs.title} showreel poster frame`} loading="lazy" />
                <div className="showreel-shade" />
                <div className="showreel-center">
                  <span className="circle-play static">
                    <Play size={18} fill="currentColor" />
                  </span>
                  <p>
                    Showreel · {cs.reelLength} · Full film shared on request
                  </p>
                </div>
              </>
            )}
          </div>
        </Reveal>
      </section>

      <section className="case-body section-pad" data-testid="case-body">
        <div className="cs-body-grid">
          <Reveal>
            <h3>The challenge</h3>
            <p>{cs.challenge}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h3>The approach</h3>
            <p>{cs.approach}</p>
          </Reveal>
          <Reveal delay={0.16}>
            <h3>The outcome</h3>
            <p>{cs.outcome}</p>
          </Reveal>
        </div>
        <Reveal className="stats-band" delay={0.1}>
          {cs.stats.map((stat) => (
            <div className="stat-box" key={stat.label} data-testid={`case-stat-${stat.label.replace(/\s+/g, "-")}`}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </Reveal>
      </section>

      <section className="case-gallery section-pad" data-testid="case-gallery">
        <Reveal className="section-heading">
          <p className="eyebrow">Frames &amp; artefacts</p>
          <span className="section-count">{cs.client}</span>
        </Reveal>
        <div className="gallery-grid">
          {cs.gallery.map((image, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <img src={image} alt={`${cs.title} — frame ${i + 1}`} loading="lazy" />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="case-credits section-pad" data-testid="case-credits">
        <Reveal className="section-heading">
          <p className="eyebrow">Credits</p>
        </Reveal>
        <div className="credits">
          {cs.credits.map((credit) => (
            <div className="credit-row" key={credit.role}>
              <span>{credit.role}</span>
              <strong>{credit.name}</strong>
            </div>
          ))}
        </div>
      </section>

      <CtaBand testid="case-cta-band" />

      <Link to={`/work/${next.slug}`} className="next-link" data-testid="case-next-link">
        <span>
          Next case study <ArrowRight size={12} style={{ verticalAlign: "-1px" }} />
        </span>
        <h3>{next.title}</h3>
      </Link>
      <div className="back-row">
        <Link to="/work" className="text-link" data-testid="case-back-link">
          <ArrowLeft size={15} /> All work
        </Link>
      </div>
    </>
  );
}
